/*
 *  -----------------------------------------------------------------------
 *    <copyright file="restore-snapshot.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect } from 'react';

import { useTranslation, useBoolean } from 'hooks';
import { RESTORE_SNAPSHOT, GET_RESTORE_SNAPSHOT_STATUS } from 'queries/snapshots';
import { DataStorageRegion } from 'types/graphql-global-types';
import NotificationService from 'utils/notification';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import TextField from 'views/components/core/text-field';

interface IRestoreSnapshot {
    open: boolean;
    onClose: Function;
    organisations: any[];
    snapshotName: string;
}

const RestoreSnapshot = ({ open, onClose, organisations, snapshotName }: IRestoreSnapshot) => {
    const { t } = useTranslation();

    const [restoreSnapshot] = useMutation(RESTORE_SNAPSHOT);
    const [loadSnapshotStatus, { data }] = useLazyQuery(GET_RESTORE_SNAPSHOT_STATUS);

    const [name, setName] = useState('');
    const [errorName, setErrorName] = useState('');

    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const timeout = useRef(null);

    useEffect(() => {
        if (data?.restoreSnapshotStatus.isFinished && !data?.restoreSnapshotStatus.error) {
            clearInterval(timeout.current);
            saving.setFalse();
            close('success');
        } else if (data?.restoreSnapshotStatus.error) {
            clearInterval(timeout.current);
            NotificationService.showNotification(data?.restoreSnapshotStatus.error);
            close();
        }
    }, [data]);

    const close = (action?: string) => {
        onClose(action);

        if (action !== 'error') {
            setErrorName('');
            setName('');
            saving.setFalse();
            disabledSave.setTrue();
        }
    };

    const onChange = (event: any) => {
        disabledSave.setFalse();
        setErrorName('');
        const { value } = event.target;

        setName(value);
    };

    const onCreate = () => {
        const org = organisations.find((item: any) => item.name === name);

        if (org) {
            setErrorName(t('admin:orgDoesNotExist'));
            return;
        }

        saving.setTrue();
        restoreSnapshot({
            variables: {
                connectionString: null,
                name,
                reason: '',
                region: DataStorageRegion.OPRA_EU,
                urlPath: name,
                useTransactions: false,
                snapshotName,
            },
        })
            .then((result: any) => {
                if (result.data.restoreSnapshot) {
                    timeout.current = setInterval(() => {
                        loadSnapshotStatus({
                            variables: {
                                statusId: result.data.restoreSnapshot,
                            },
                        });
                    }, 2000);
                }
            })
            .catch((error: any) => {
                console.log('error restoring snapshot', error);
                saving.setFalse();
                close('error');
            });
    };

    return (
        <CustomDialog
            className="restore-snapshot-dialog"
            onClose={() => close()}
            fullWidth
            scroll="paper"
            maxWidth="sm"
            open={open}
        >
            <DialogTitle id="restore-snapshot-modal-title">
                {t('admin:restoreSnapshot')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content" style={{ padding: 15 }}>
                {!saving.value ? (
                    <>
                        <p className="info-text">{t('admin:enterNameOfOrganisation')}</p>
                        <TextField
                            className="mb-20"
                            id="org-name"
                            label={`${t('common:name')}*`}
                            value={name}
                            placeholder={`${t('enterName')}*`}
                            fullWidth
                            error={!!errorName}
                            variant="outlined"
                            onChange={onChange}
                            helperText={<CharactersRemaining id="org-name-text" error={errorName} />}
                        />
                    </>
                ) : (
                    <p className="loading-state-text">{t('admin:restoringSnapshot')}</p>
                )}
            </DialogContent>
            <DialogActions style={{ padding: 15 }}>
                <SaveButton
                    disabled={disabledSave.value || errorName || saving.value}
                    id="restore-snapshot-btn"
                    text={t('admin:restore')}
                    onClick={onCreate}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default RestoreSnapshot;
